import React, { useRef, useEffect, useState } from "react";
import { BASE_URL } from "utils/constant";
import { fetchOrganizationMembers } from "../../../api/design/service";
import { X, UploadCloud, FileText } from "lucide-react";
import { IoCloseOutline } from "react-icons/io5";
import { useAlert } from "components/UI_components/Alert";

export default function CreateDesignModal({
  show,
  onClose,
  formData,
  setFormData,
  designTypes,
  onSubmit,
}) {
  const fileInputRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orgMembers, setOrgMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isCustomType, setIsCustomType] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { addAlert } = useAlert();

  const processFile = (file) => {
    if (file) {
      const name = String(file.name || "").toLowerCase();
      const isPdf = name.endsWith(".pdf") || file.type === "application/pdf";
      const isImage =
        (file.type || "").startsWith("image/") || /\.(png|jpe?g)$/i.test(name);
      const isDwg = name.endsWith(".dwg") || file.type === "application/dwg";
      if (!isPdf && !isImage && !isDwg) {
        addAlert("Only JPG, PNG, PDF, or DWG files are supported", "warning");
        setUploadedFile(null);
        setFormData({ ...formData, uploadedFileName: "" });
        return false;
      }
      setUploadedFile(file);
      setFormData({ ...formData, uploadedFileName: file.name });
      return true;
    }
    return false;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (processFile(file)) {
      e.target.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setUploadedFile(null);
    setFormData({ ...formData, uploadedFileName: "" });
  };

  useEffect(() => {
    const fetchSites = async () => {
      try {
        setLoadingSites(true);
        const org = JSON.parse(localStorage.getItem("organization") || "{}");
        const orgId = org?.id;
        if (!orgId) {
          setSites([]);
          return;
        }
        const accessToken = localStorage.getItem("access");
        const res = await fetch(
          `${BASE_URL}/sites/organizations/${orgId}/sites/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        const json = await res.json();
        const list = Array.isArray(json)
          ? json
          : json?.data || json?.results || [];
        setSites(list);
      } catch (err) {
        console.error("Failed to load sites", err);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        setLoadingMembers(true);
        const org = JSON.parse(localStorage.getItem("organization") || "{}");
        const orgId = org?.id;
        if (!orgId) {
          setOrgMembers([]);
          return;
        }
        const list = await fetchOrganizationMembers(orgId);
        setOrgMembers(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load organization members", err);
        setOrgMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };
    loadMembers();
  }, []);

  const assignToIds = Array.isArray(formData.assign_to)
    ? formData.assign_to
    : [];

  const handleToggleAssignee = (memberId) => {
    const nextIds = assignToIds.includes(memberId)
      ? assignToIds.filter((id) => id !== memberId)
      : [...assignToIds, memberId];

    const display = (orgMembers || [])
      .filter((m) => nextIds.includes(Number(m.id)))
      .map((m) => {
        const fn = m?.user?.first_name || "";
        const ln = m?.user?.last_name || "";
        const full = `${fn} ${ln}`.trim();
        return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
      })
      .join(", ");
    setFormData({ ...formData, assign_to: nextIds, assignee: display });
  };

  const handleCreate = async () => {
    if (
      !formData.name ||
      !formData.projectName ||
      !formData.fileType ||
      !formData.format ||
      !formData.siteId ||
      !uploadedFile
    ) {
      addAlert("Please fill all required fields", "warning");
      return;
    }

    try {
      setSubmitting(true);
      const org = JSON.parse(localStorage.getItem("organization") || "{}");
      const accessToken = localStorage.getItem("access");
      let res;
      if (uploadedFile) {
        const formPayload = new FormData();
        formPayload.append("site", String(formData.siteId));
        formPayload.append("organization", String(org?.id));
        formPayload.append("design_name", formData.name);
        formPayload.append("type", formData.fileType);
        formPayload.append("format", formData.format);
        formPayload.append("status", "draft");
        formPayload.append("diagram", uploadedFile);
        (assignToIds || []).forEach((id) => {
          if (id || id === 0)
            formPayload.append("assign_to", String(Number(id)));
        });

        res = await fetch(`${BASE_URL}/design/designs/create/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formPayload,
        });
      } else {
        const payload = {
          site: Number(formData.siteId),
          organization: Number(org?.id),
          design_name: formData.name,
          type: formData.fileType,
          format: formData.format,
          diagram: null,
          status: "draft",
          assign_to: assignToIds.map((x) => Number(x)),
        };

        res = await fetch(`${BASE_URL}/design/designs/create/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        });
      }
      const data = await res.json();
      if (!res.ok) {
        const errMsg =
          data?.detail || data?.message || "Failed to create design";
        addAlert(errMsg, "error");
        return;
      }

      const selectedSite = sites.find(
        (s) => String(s.id) === String(formData.siteId),
      );
      onSubmit(
        data,
        {
          name: formData.name,
          fileType: formData.fileType,
          format: formData.format,
          assignee: formData.assignee || "Unassigned",
          assign_to: assignToIds,
        },
        selectedSite,
      );
      setFormData({
        name: "",
        projectName: "",
        assignee: "",
        assign_to: [],
        fileType: "",
        format: "",
        uploadedFileName: "",
        siteId: "",
      });
      setUploadedFile(null);
      setIsCustomType(false);
      onClose();
      addAlert("Design Created Successfully", "success");
    } catch (error) {
      console.error("Create design error:", error);
      addAlert("Failed to create design", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const resetCreateDesignForm = () => {
    setFormData({
      name: "",
      projectName: "",
      assignee: "",
      assign_to: [],
      fileType: "",
      format: "",
      uploadedFileName: "",
      siteId: "",
    });
    setUploadedFile(null);
    setIsCustomType(false);
    setIsDragging(false);
  };

  const handleCloseModal = () => {
    resetCreateDesignForm();
    onClose();
  };

  const isCreateDisabled =
    !formData.name ||
    !formData.projectName ||
    !formData.fileType ||
    !formData.format ||
    !formData.siteId ||
    !uploadedFile ||
    submitting;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-stretch justify-end z-50"
      onClick={handleCloseModal}
    >
      <div
        className="bg-white w-full sm:max-w-lg h-full overflow-hidden flex flex-col rounded-l-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10 relative">
          <h2 className="text-xl font-semibold text-gray-900 text-center">
            Create New Design
          </h2>
          {/* <p className="text-sm text-gray-500 mt-1">
            Add details, assign team members, and upload your design file.
          </p> */}
          <button
            className="text-gray-500 cursor-pointer hover:text-gray-700 absolute right-6 top-1/2 -translate-y-1/2"
            onClick={handleCloseModal}
          >
            <IoCloseOutline size={20} />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5 pb-24 bg-gray-50/40"
          style={{ scrollbarWidth: "none" }}
        >
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Basic Details
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Design Name *
              </label>
              <input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full border border-gray-300 h-9 px-3 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                placeholder="Enter design name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Select Project *
              </label>
              <select
                value={formData.siteId || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selected = sites.find(
                    (s) => String(s.id) === String(selectedId),
                  );
                  setFormData({
                    ...formData,
                    siteId: selectedId,
                    projectName: selected?.site_name || "",
                  });
                }}
                className="w-full border border-gray-300 h-9 px-3 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-sm-primary"
              >
                <option value="">
                  {loadingSites ? "Loading projects..." : "Select a project"}
                </option>
                {sites.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.site_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Team Assignment
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Assignee
              </label>
              <div className="w-full border border-gray-300 rounded-md p-2 max-h-48 overflow-y-auto space-y-1 bg-white">
                {(orgMembers || []).length === 0 ? (
                  <div className="text-sm text-gray-500 py-2 px-1 text-center">
                    {loadingMembers ? "Loading members..." : "No members found"}
                  </div>
                ) : (
                  (orgMembers || []).map((m) => {
                    const fn = m?.user?.first_name || "";
                    const ln = m?.user?.last_name || "";
                    const full = `${fn} ${ln}`.trim();
                    const label =
                      full ||
                      m?.user?.username ||
                      m?.user?.email ||
                      `Member ${m?.id}`;
                    const isChecked = assignToIds.includes(m.id);
                    return (
                      <label
                        key={m.id}
                        className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleAssignee(m.id)}
                          className="w-4 h-4 rounded border-gray-300 text-sm-primary focus:ring-sm-primary cursor-pointer"
                        />
                        <span
                          className={`text-sm ${
                            isChecked
                              ? "text-gray-900 font-medium"
                              : "text-gray-600"
                          }`}
                        >
                          {label}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider font-semibold">
                Selected: {formData.assignee || "None"}
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Design Setup
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Type *
              </label>
              {!isCustomType ? (
                <select
                  value={formData.fileType}
                  onChange={(e) => {
                    if (e.target.value === "custom_add") {
                      setIsCustomType(true);
                      setFormData({ ...formData, fileType: "" });
                    } else {
                      setFormData({ ...formData, fileType: e.target.value });
                    }
                  }}
                  className="w-full border border-gray-300 h-9 px-3 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-sm-primary"
                >
                  <option value="">Select type</option>
                  {designTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option
                    value="custom_add"
                    className="text-sm-primary font-medium"
                  >
                    + Add Custom Type
                  </option>
                </select>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={formData.fileType}
                    onChange={(e) =>
                      setFormData({ ...formData, fileType: e.target.value })
                    }
                    className="w-full border border-gray-300 h-9 px-3 rounded-md focus:ring-1 focus:ring-sm-primary outline-none"
                    placeholder="Enter custom type"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomType(false);
                      setFormData({ ...formData, fileType: "" });
                    }}
                    className="px-3 border rounded text-gray-500 hover:bg-gray-50 transition-colors"
                    title="Back to list"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Format *
              </label>
              <select
                value={formData.format || ""}
                onChange={(e) =>
                  setFormData({ ...formData, format: e.target.value })
                }
                className="w-full border border-gray-300 h-9 px-3 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-sm-primary"
              >
                <option value="">Select format</option>
                <option value="2d">2d</option>
                <option value="3d">3d</option>
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              File Upload
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Diagram *
              </label>
              {!uploadedFile ? (
                <div
                  className={`border-2 border-dashed rounded-lg p-7 flex flex-col items-center justify-center text-center text-sm cursor-pointer transition-colors ${
                    isDragging
                      ? "border-sm-primary bg-sm-primary/5 text-sm-primary"
                      : "border-gray-300 text-gray-500"
                  }`}
                  onClick={() =>
                    fileInputRef.current && fileInputRef.current.click()
                  }
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <UploadCloud className="w-8 h-8 mb-2" />
                  <div className="font-medium">Click or drag to upload</div>
                  <div className="text-xs text-gray-400 mt-1">
                    Supports: JPG, PNG, PDF, DWG
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.dwg"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              ) : (
                <div className="border border-sm-primary/20 bg-sm-primary/5 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="bg-sm-primary/10 p-2 rounded text-sm-primary">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {formData.uploadedFileName}
                      </div>
                      <div className="text-xs text-gray-500 uppercase">
                        File Attached
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="p-1.5 hover:bg-white rounded-full text-gray-400 hover:text-red-500 transition-colors shadow-sm"
                    title="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-3 bg-white border-t border-gray-200 flex justify-end gap-2 shrink-0">
          <button
            className="px-4 h-9 text-sm font-medium border rounded"
            onClick={handleCloseModal}
          >
            Cancel
          </button>
          <button
            className={`px-4 h-9 text-sm font-medium text-white rounded transition-colors ${
              isCreateDisabled
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-sm-primary hover:bg-sm-hover"
            }`}
            onClick={handleCreate}
            disabled={isCreateDisabled}
          >
            {submitting ? "Creating..." : "Create Design"}
          </button>
        </div>
      </div>
    </div>
  );
}
